import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { AlertTriangle } from 'lucide-react';

// SURPLUS LINES RATES, EDITABLE (wave 4 item 1, 2026-07-31). David, after a day
// of quote entry: the rates for FL, KY, WV and NJ should not be hardcoded and he
// wants somewhere to see and change them.
//
// HE ADDS A STATE BY TYPING IT, not by asking for a deploy, so the list is not
// limited to the four he named.
//
// THE UNSET BASIS IS SAID IN WORDS, not implied by an empty select. A rate on
// file that suggests nothing looks, from the quote screen, exactly like a rate
// that is missing, and the difference is one dropdown. Every row that carries a
// rate and no basis says so here in a sentence.

const BASES = [
  { value: '', label: 'not set' },
  { value: 'premium', label: 'premium' },
  { value: 'premium_plus_fees', label: 'premium plus fees' },
];

const inputCls = 'h-9 w-full rounded-card border border-border bg-bg px-2 text-sm text-text';

export default function TaxesAdmin() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [savingState, setSavingState] = useState('');
  const [newState, setNewState] = useState('');

  const refresh = useCallback(async () => {
    try {
      const d = await api.get('/api/admin/tax-rates');
      setRows(Array.isArray(d?.rates) ? d.rates : []);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load the rate table');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (state, patch) => {
    setSavingState(state);
    try {
      await api.put(`/api/admin/tax-rates/${encodeURIComponent(state)}`, patch);
      await refresh();
      toast.success(`${state} saved.`);
    } catch (e) {
      toast.error(e.message || `Could not save ${state}`);
    } finally {
      setSavingState('');
    }
  }, [refresh]);

  const addState = useCallback(async () => {
    const st = newState.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(st)) { toast.error('A state is a two-letter code, e.g. KY.'); return; }
    if ((rows || []).some((r) => r.state === st)) { toast.error(`${st} is already listed.`); return; }
    // Added with NOTHING set. A new state is a question, not an answer.
    await save(st, { rate_percent: null, basis: null, confirmed: false });
    setNewState('');
  }, [newState, rows, save]);

  return (
    <div className="p-4">
      <div>
        <h2 className="text-xl font-semibold text-text">State taxes</h2>
        <p className="mt-1 text-sm text-text-muted">
          Surplus lines rates. The quote wizard offers the arithmetic as a chip and never writes it.
        </p>
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-card border border-border bg-bg/40 px-3 py-2 text-[11.5px] text-text-muted">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-text-dim" />
        <span>
          A rate suggests nothing until it is <strong className="text-text">confirmed</strong> and its
          {' '}<strong className="text-text">basis</strong> is set. Tax on premium alone and tax on premium
          plus fees are different numbers, so there is no default: the basis is yours to choose.
          The state comes from the aircraft&rsquo;s base airport, never from a mailing address.
        </span>
      </div>

      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {rows === null && !error ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}

      {rows ? (
        <div className="mt-4 space-y-2">
          {rows.map((r) => (
            <RateRow key={r.state} row={r} busy={savingState === r.state} onSave={save} />
          ))}
          {rows.length === 0 ? (
            <p className="text-sm text-text-muted">No states on file yet.</p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-end gap-2 border-t border-border pt-4">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          Add a state
          <Input
            value={newState}
            onChange={(e) => setNewState(e.target.value)}
            placeholder="KY"
            maxLength={2}
            className="mt-1 w-20 uppercase"
          />
        </label>
        <Button type="button" variant="outline" onClick={addState}>Add</Button>
      </div>
    </div>
  );
}

function RateRow({ row, busy, onSave }) {
  const [ratePct, setRatePct] = useState(row.rate_percent == null ? '' : String(row.rate_percent));
  const [basis, setBasis] = useState(row.basis || '');
  const [confirmed, setConfirmed] = useState(row.confirmed === true);
  const [effectiveFrom, setEffectiveFrom] = useState(row.effective_from || '');
  const [citation, setCitation] = useState(row.citation || '');
  const [notes, setNotes] = useState(row.notes || '');

  // What this row does on a quote TODAY, stated rather than inferred. The three
  // silent states (no rate, unconfirmed, no basis) are indistinguishable from
  // the quote screen and each has a different fix.
  const verdict = row.rate == null
    ? 'No rate on file. Nothing is suggested.'
    : row.confirmed !== true
      ? `Not confirmed. Nothing is suggested.${row.citation ? '' : ' Confirming it needs a citation.'}`
      : !row.basis
        ? `${row.rate_percent}% on file, but the basis is not set, so nothing is suggested.`
        : `Suggests ${row.rate_percent}% of ${row.basis === 'premium' ? 'premium' : 'premium plus fees'}.`;
  const live = row.rate != null && row.confirmed === true && !!row.basis;

  return (
    <div className="rounded-card border border-border bg-bg/30 p-3">
      <div className="flex flex-wrap items-end gap-3">
        <span className="w-10 text-lg font-semibold text-text">{row.state}</span>

        <label className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          Rate
          <div className="mt-1 flex items-center gap-1">
            <Input value={ratePct} onChange={(e) => setRatePct(e.target.value)} placeholder="1" className="w-20" />
            <span className="text-sm text-text-muted">%</span>
          </div>
        </label>

        <label className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          Basis
          <select value={basis} onChange={(e) => setBasis(e.target.value)} className={`mt-1 ${inputCls} w-44`}>
            {BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </label>

        <label className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          In effect since
          <Input value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} placeholder="YYYY-MM-DD" className="mt-1 w-36" />
        </label>

        <label className="flex h-9 items-center gap-2 rounded-card border border-border bg-bg px-3">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="h-4 w-4 rounded border-border bg-bg accent-accent"
          />
          <span className="text-xs text-text-muted">Confirmed</span>
        </label>

        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => onSave(row.state, {
            rate_percent: ratePct,
            basis: basis || null,
            confirmed,
            citation,
            effective_from: effectiveFrom,
            notes,
          })}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {/* CONFIRMED MEANS CONFIRMED AGAINST A CITED SOURCE (2026-08-01). FL and
          WV were confirmed on David stating the figures from memory and
          restating them when asked, which tests recall rather than
          correctness. Both were right; the provenance was the wrong kind. */}
      <label className="mt-2 block text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        Citation {confirmed ? <span className="text-accent">required to confirm</span> : null}
        <Input
          value={citation}
          onChange={(e) => setCitation(e.target.value)}
          placeholder="the statute, rule or filing this rate comes from"
          className="mt-1"
        />
      </label>

      <label className="mt-2 block text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        Notes
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="anything else worth recording" className="mt-1" />
      </label>

      <p className={`mt-2 text-[11.5px] ${live ? 'text-success' : 'text-text-dim'}`}>{verdict}</p>
    </div>
  );
}
